import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { formatTimestampDate } from '../../../utils/dateTime';
import TransmittalWatermark from './TransmittalWatermark';

/**
 * Request For Information - a rebuild of the BU28-SOP-100 print form.
 *
 * Same page box as the Submittal Transmittal (LETTER portrait, 18pt margins, 576pt usable), so
 * the transmittal forms share one geometry. The whole form sits inside one heavy frame, split
 * into bands by full-width rules: header, project block, Respondent | Author, the three long
 * text boxes, then Attachments | Impact.
 *
 * The letterhead logos (top left and top right on the source) are not placed yet - their
 * boxes are kept so the title stays centred where the form puts it. The Impact column is one
 * choice, not four flags: the chosen box prints a literal X, as on the paper form.
 *
 * Duplicated from the transmittal document rather than imported from the reports pipeline,
 * which is shared by the locked I/O Assignments export.
 */

const W = 576;
const FRAME = 1.5;
const THIN = 0.75;
const BORDER = '#000';
const PAD = 10;          // inner padding of each band
const INNER = W - FRAME * 2 - PAD * 2;

const s = StyleSheet.create({
  page:      { paddingTop: 18, paddingBottom: 18, paddingHorizontal: 18, fontFamily: 'Helvetica', fontSize: 9 },
  frame:     { width: W, borderWidth: FRAME, borderColor: BORDER },
  band:      { paddingHorizontal: PAD, paddingVertical: 6, borderBottomWidth: FRAME, borderColor: BORDER },
  bandLast:  { paddingHorizontal: PAD, paddingVertical: 6 },
  row:       { flexDirection: 'row' },

  logoBox:   { width: 150, height: 30 },
  titleBox:  { flexGrow: 1, alignItems: 'center' },
  title:     { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  subtitle:  { fontSize: 9, marginTop: 1 },

  lbl:       { fontSize: 9, textAlign: 'right', paddingRight: 4, paddingTop: 3 },
  lblBold:   { fontFamily: 'Helvetica-Bold' },
  line:      { borderBottomWidth: THIN, borderColor: BORDER, fontSize: 9, paddingTop: 3, paddingLeft: 2, minHeight: 13 },

  secLabel:  { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  box:       { borderWidth: THIN, borderColor: BORDER, padding: 4, fontSize: 9 },
  gridCell:  { borderBottomWidth: 0.5, borderColor: '#bbb', minHeight: 14, paddingHorizontal: 3, paddingTop: 3, fontSize: 9 },

  footer:    { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, fontSize: 7 },
});

// Project block: left column of label/value pairs, right column beside it. Project Title
// spans the full width on its own row, as on the form.
const LEFT_LBL_W = 110;
const LEFT_VAL_W = 190;
const RIGHT_LBL_W = 110;
const RIGHT_VAL_W = INNER - LEFT_LBL_W - LEFT_VAL_W - RIGHT_LBL_W;

const Pair = ({ label, value, labelW, valueW, bold }) => (
  <View style={s.row}>
    <Text style={[s.lbl, { width: labelW }, bold ? s.lblBold : null]}>{label}</Text>
    <Text style={[s.line, { width: valueW }]}>{value || ' '}</Text>
  </View>
);

// Respondent | Author: two ruled five-line boxes. Respondent is the transfer party (a single
// name); Author is free text. The name prints on the first line and the rest stay ruled.
const PEOPLE_LINES = 5;
const PeopleBox = ({ value, width }) => (
  <View style={[s.box, { width, padding: 0 }]}>
    {Array.from({ length: PEOPLE_LINES }, (_, i) => (
      <Text key={i} style={[s.gridCell, i === PEOPLE_LINES - 1 ? { borderBottomWidth: 0 } : null]}>
        {i === 0 ? (value || ' ') : ' '}
      </Text>
    ))}
  </View>
);

const TextBox = ({ label, value, height }) => (
  <View style={s.band}>
    <Text style={s.secLabel}>{label}</Text>
    <View style={[s.box, { minHeight: height }]}>
      <Text>{value || ' '}</Text>
    </View>
  </View>
);

const IMPACTS = ['Drawing', 'Specification', 'Cost', 'Schedule'];
const ATTACH_ROWS = 3;

export const RFI_IMPACT_OPTIONS = IMPACTS;

const RfiPdfDocument = ({ data = {}, watermark = null }) => {
  const halfW = (INNER - 8) / 2;
  const attachDescW = 250;
  const attachFileW = 230;
  const printDate = formatTimestampDate(new Date());

  return (
    <Document title={`Request For Information ${data.transmittalNumber || ''}`.trim()}>
      <Page size="LETTER" orientation="portrait" style={s.page}>
        <TransmittalWatermark text={watermark} />

        <View style={s.frame}>
          {/* Header: logo | title | logo. Logos are not placed yet; the boxes hold the title centred. */}
          <View style={[s.band, s.row, { alignItems: 'center' }]}>
            <View style={s.logoBox} />
            <View style={s.titleBox}>
              <Text style={s.title}>Request For Information</Text>
              <Text style={s.subtitle}>Response</Text>
            </View>
            <View style={s.logoBox} />
          </View>

          {/* Project block */}
          <View style={[s.band, { paddingVertical: 8 }]}>
            <Pair label="Project Title:" value={data.projectTitle} bold
              labelW={LEFT_LBL_W} valueW={INNER - LEFT_LBL_W} />
            <View style={s.row}>
              <View>
                <Pair label="Client:"         value={data.client}            labelW={LEFT_LBL_W} valueW={LEFT_VAL_W} />
                <Pair label="Owner:"          value={data.owner}             labelW={LEFT_LBL_W} valueW={LEFT_VAL_W} />
                <Pair label="RFI Number:"     value={data.transmittalNumber} labelW={LEFT_LBL_W} valueW={LEFT_VAL_W} />
                <Pair label="Project Number:" value={data.projectNumber}     labelW={LEFT_LBL_W} valueW={LEFT_VAL_W} />
              </View>
              <View>
                <Pair label="Date:"           value={data.date}          labelW={RIGHT_LBL_W} valueW={RIGHT_VAL_W} />
                <Pair label="Discipline:"     value={data.discipline}    labelW={RIGHT_LBL_W} valueW={RIGHT_VAL_W} />
                <Pair label="Building/Area:"  value={data.buildingArea}  labelW={RIGHT_LBL_W} valueW={RIGHT_VAL_W} />
                <Pair label="Reference:"      value={data.reference}     labelW={RIGHT_LBL_W} valueW={RIGHT_VAL_W} />
              </View>
            </View>
          </View>

          {/* Respondent | Author */}
          <View style={s.band}>
            <View style={s.row}>
              <Text style={[s.secLabel, { width: halfW + 8 }]}>Respondent:</Text>
              <Text style={[s.secLabel, { width: halfW }]}>Author:</Text>
            </View>
            <View style={s.row}>
              <PeopleBox value={data.respondent} width={halfW} />
              <View style={{ width: 8 }} />
              <PeopleBox value={data.author} width={halfW} />
            </View>
          </View>

          <TextBox label="Information Requested:" value={data.informationRequested} height={140} />
          <TextBox label="Proposed Solution:"     value={data.proposedSolution}     height={140} />
          <TextBox label="Response:"              value={data.response}             height={180} />

          {/* Attachments | Impact */}
          <View style={[s.bandLast, s.row]}>
            <View style={{ width: INNER - 110 }}>
              <Text style={s.secLabel}>Attachments:</Text>
              <View style={s.row}>
                <Text style={{ width: 14 }}>#</Text>
                <Text style={{ width: attachDescW }}>Description:</Text>
                <Text style={{ width: attachFileW }}>File Name:</Text>
              </View>
              {Array.from({ length: ATTACH_ROWS }, (_, i) => (
                <View style={s.row} key={i}>
                  <Text style={{ width: 14, paddingTop: 3 }}>{i + 1}</Text>
                  <Text style={[s.gridCell, { width: attachDescW, borderLeftWidth: THIN, borderColor: BORDER },
                                i === 0 ? { borderTopWidth: THIN } : null,
                                i === ATTACH_ROWS - 1 ? { borderBottomWidth: THIN, borderColor: BORDER } : null]}>
                    {' '}
                  </Text>
                  <Text style={[s.gridCell, { width: attachFileW, borderLeftWidth: THIN, borderRightWidth: THIN, borderColor: BORDER },
                                i === 0 ? { borderTopWidth: THIN } : null,
                                i === ATTACH_ROWS - 1 ? { borderBottomWidth: THIN, borderColor: BORDER } : null]}>
                    {i === 0 ? (data.attachmentUrl || ' ') : ' '}
                  </Text>
                </View>
              ))}
            </View>
            <View style={{ width: 110 }}>
              <Text style={s.secLabel}>Impact:</Text>
              <View style={{ height: 12 }} />
              {IMPACTS.map((opt, i) => (
                <View style={s.row} key={opt}>
                  <Text style={{ width: 28, height: 14, borderLeftWidth: THIN, borderRightWidth: THIN, borderTopWidth: THIN,
                                 borderBottomWidth: i === IMPACTS.length - 1 ? THIN : 0, borderColor: BORDER,
                                 textAlign: 'center', fontFamily: 'Helvetica-Bold', paddingTop: 2 }}>
                    {data.impact === opt ? 'X' : ' '}
                  </Text>
                  <Text style={{ paddingLeft: 3, paddingTop: 2 }}>{opt}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={s.footer}>
          <Text>Print Date: {printDate}</Text>
          <Text>Wunderlich-Malec Confidential</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default RfiPdfDocument;
